import { searchHotelDestinations } from '@/src/api/requests/search-hotels'
import type { HotelDestinationPayload } from '@/src/api/requests/search-hotels'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { buildHotelSearchUrl } from '../../utils/buildHotelSearchUrl'

export interface HotelRoomConfig {
    adults: number
    children: number
}

export type HotelDestinationKind = 'city' | 'hotel'

export type { HotelDestinationPayload }

export interface HotelDestinationSuggestion {
    key: string
    kind: HotelDestinationKind
    text: string
    provinceName: string
    countryName: string
    label: string
    payload: HotelDestinationPayload
}

const MIN_ROOMS = 1
const MAX_ROOMS = 8
const MIN_ADULTS_PER_ROOM = 1
const MAX_ADULTS_PER_ROOM = 6
const MIN_CHILDREN_PER_ROOM = 0
const MAX_CHILDREN_PER_ROOM = 4

const DEFAULT_ROOM_CONFIG: HotelRoomConfig = { adults: 1, children: 0 }

const addDays = (date: Date, days: number) => dayjs(date).add(days, 'day').toDate()

const startOfDay = (date: Date) => dayjs(date).startOf('day')

const isSameOrBefore = (a: Date, b: Date) =>
    startOfDay(a).isBefore(startOfDay(b)) || startOfDay(a).isSame(startOfDay(b))

export const useHotelSearchManager = () => {
    const [destination, setDestination] = useState('')
    const [selectedDestinationPayload, setSelectedDestinationPayload] = useState<HotelDestinationPayload | null>(null)
    const [selectedDestinationKind, setSelectedDestinationKind] = useState<HotelDestinationKind | null>(null)
    const [destinationSuggestions, setDestinationSuggestions] = useState<HotelDestinationSuggestion[]>([])
    const [destinationLoading, setDestinationLoading] = useState(false)

    const destinationRequestIdRef = useRef(0)
    const selectingDestinationRef = useRef(false)

    const handleDestinationInputChange = useCallback((nextText: string) => {
        selectingDestinationRef.current = false
        setDestination(nextText)
        setSelectedDestinationPayload(null)
        setSelectedDestinationKind(null)
    }, [])

    const handleDestinationSelect = useCallback((suggestion: HotelDestinationSuggestion) => {
        selectingDestinationRef.current = true
        setDestination(suggestion.label)
        setSelectedDestinationPayload(suggestion.payload)
        setSelectedDestinationKind(suggestion.kind)
        setDestinationSuggestions([])
    }, [])

    useEffect(() => {
        const q = destination.trim()

        if (selectingDestinationRef.current) {
            selectingDestinationRef.current = false
            return
        }

        if (q.length < 2) {
            setDestinationSuggestions([])
            setDestinationLoading(false)
            return
        }

        const requestId = ++destinationRequestIdRef.current
        const timer = setTimeout(async () => {
            setDestinationLoading(true)
            try {
                const data = await searchHotelDestinations(q)
                if (requestId !== destinationRequestIdRef.current) return

                const normalize = (items: typeof data.cities, kind: HotelDestinationKind, keyPrefix: string) =>
                    (items || []).map((item, idx) => {
                        const provinceName = item.payload?.provinceName || ''
                        const countryName = item.payload?.countryName || ''
                        const label = `${item.text}, ${provinceName}, ${countryName}`.trim().replace(/\s+/g, ' ')
                        return {
                            key: `${keyPrefix}_${idx}_${item.payload?.cityId || item.payload?.uniqueId || item.text}`,
                            kind,
                            text: item.text,
                            provinceName,
                            countryName,
                            label,
                            payload: item.payload
                        }
                    })

                const cities = normalize(data.cities, 'city', 'city')
                const hotels = normalize(data.hotels, 'hotel', 'hotel')
                setDestinationSuggestions([...cities, ...hotels].slice(0, 8))
            } catch {
                if (requestId !== destinationRequestIdRef.current) return
                setDestinationSuggestions([])
            } finally {
                if (requestId !== destinationRequestIdRef.current) return
                setDestinationLoading(false)
            }
        }, 300)

        return () => clearTimeout(timer)
    }, [destination])

    const [checkIn, setCheckIn] = useState<Date | null>(null)
    const [checkOut, setCheckOut] = useState<Date | null>(null)
    const [roomConfigs, setRoomConfigs] = useState<HotelRoomConfig[]>([{ ...DEFAULT_ROOM_CONFIG }])

    const totalRooms = roomConfigs.length
    const totalAdults = useMemo(() => roomConfigs.reduce((sum, r) => sum + r.adults, 0), [roomConfigs])
    const totalChildren = useMemo(() => roomConfigs.reduce((sum, r) => sum + r.children, 0), [roomConfigs])
    const totalGuests = totalAdults + totalChildren

    const nights = useMemo(() => {
        if (!checkIn || !checkOut) return null
        const diff = startOfDay(checkOut).diff(startOfDay(checkIn), 'day')
        return diff > 0 ? diff : null
    }, [checkIn, checkOut])

    const handleCheckInChange = useCallback((date: Date | null) => {
        setCheckIn(date)
        if (!date) return

        setCheckOut(prev => {
            if (prev && isSameOrBefore(prev, date)) {
                return null
            }
            return prev
        })
    }, [])

    const handleCheckOutChange = useCallback(
        (date: Date | null) => {
            if (!date) {
                setCheckOut(null)
                return
            }
            if (checkIn && isSameOrBefore(date, checkIn)) {
                return
            }
            setCheckOut(date)
        },
        [checkIn]
    )

    const handleRoomCountChange = useCallback((count: number) => {
        const next = Math.min(MAX_ROOMS, Math.max(MIN_ROOMS, count))
        setRoomConfigs(prev => {
            if (next === prev.length) return prev
            if (next > prev.length) {
                const added = Array.from({ length: next - prev.length }, () => ({ ...DEFAULT_ROOM_CONFIG }))
                return [...prev, ...added]
            }
            return prev.slice(0, next)
        })
    }, [])

    const handleRoomAdultsChange = useCallback((roomIndex: number, adults: number) => {
        const val = Math.min(MAX_ADULTS_PER_ROOM, Math.max(MIN_ADULTS_PER_ROOM, adults))
        setRoomConfigs(prev =>
            prev.map((room, i) => (i === roomIndex ? { ...room, adults: val } : room))
        )
    }, [])

    const handleRoomChildrenChange = useCallback((roomIndex: number, children: number) => {
        const val = Math.min(MAX_CHILDREN_PER_ROOM, Math.max(MIN_CHILDREN_PER_ROOM, children))
        setRoomConfigs(prev =>
            prev.map((room, i) => (i === roomIndex ? { ...room, children: val } : room))
        )
    }, [])

    const getSearchParams = useCallback(
        () => ({
            destination,
            destinationPayload: selectedDestinationPayload,
            checkIn,
            checkOut,
            nights,
            roomConfigs,
            totalRooms,
            totalAdults,
            totalChildren,
            totalGuests
        }),
        [
            destination,
            selectedDestinationPayload,
            checkIn,
            checkOut,
            nights,
            roomConfigs,
            totalRooms,
            totalAdults,
            totalChildren,
            totalGuests
        ]
    )

    const isSearchReady = Boolean(
        selectedDestinationPayload && selectedDestinationKind && checkIn && checkOut && nights
    )

    const minCheckOutDate = checkIn ? addDays(checkIn, 1) : new Date()

    const getHotelSearchUrl = useCallback(
        (locale: string) => {
            if (!selectedDestinationPayload || !selectedDestinationKind || !checkIn || !checkOut || !nights) {
                return null
            }

            return buildHotelSearchUrl({
                kind: selectedDestinationKind,
                payload: selectedDestinationPayload,
                destinationName: selectedDestinationPayload.cityName || destination.split(',')[0]?.trim() || destination,
                checkIn,
                checkOut,
                nights,
                roomConfigs,
                locale
            })
        },
        [
            selectedDestinationPayload,
            selectedDestinationKind,
            destination,
            checkIn,
            checkOut,
            nights,
            roomConfigs
        ]
    )

    const performHotelSearch = useCallback(
        (locale: string) => {
            const url = getHotelSearchUrl(locale)
            if (!url) return false
            window.location.href = url
            return true
        },
        [getHotelSearchUrl]
    )

    return {
        destination,
        handleDestinationInputChange,
        handleDestinationSelect,
        destinationSuggestions,
        destinationLoading,
        selectedDestinationPayload,
        checkIn,
        checkOut,
        nights,
        roomConfigs,
        totalRooms,
        totalAdults,
        totalChildren,
        totalGuests,
        handleCheckInChange,
        handleCheckOutChange,
        handleRoomCountChange,
        handleRoomAdultsChange,
        handleRoomChildrenChange,
        getSearchParams,
        getHotelSearchUrl,
        performHotelSearch,
        isSearchReady,
        minCheckOutDate,
        minRooms: MIN_ROOMS,
        maxRooms: MAX_ROOMS,
        minAdultsPerRoom: MIN_ADULTS_PER_ROOM,
        maxAdultsPerRoom: MAX_ADULTS_PER_ROOM,
        minChildrenPerRoom: MIN_CHILDREN_PER_ROOM,
        maxChildrenPerRoom: MAX_CHILDREN_PER_ROOM
    }
}
